import { z } from "zod";
import type { AuthResponse } from "@markeroo/shared";

import { apiAxios } from "./config";

const UserListItemSchema = z.object({
  id: z.string().uuid(),
  email: z.string(),
  fullName: z.string(),
  createdAt: z.string(),
  isInternal: z.boolean(),
  isSuperuser: z.boolean(),
});

export type UserListItem = z.infer<typeof UserListItemSchema>;

const UserListSchema = z.object({
  items: z.array(UserListItemSchema),
});

// Raw API response type
interface RawAuthUser {
  id: string;
  email: string;
  full_name: string;
  picture_url?: string | null;
  created_at: string;
  is_superuser?: boolean;
  impersonator?: RawAuthUser | null;
}

interface RawAuthResponse {
  access_token: string;
  token_type: string;
  user: RawAuthUser;
}

// Helper to transform snake_case API response to camelCase
function transformAuthResponse(rawData: RawAuthResponse): AuthResponse {
  return {
    accessToken: rawData.access_token,
    tokenType: "bearer",
    user: {
      id: rawData.user.id,
      email: rawData.user.email,
      fullName: rawData.user.full_name,
      pictureUrl: rawData.user.picture_url ?? null,
      createdAt: rawData.user.created_at,
      isSuperuser: rawData.user.is_superuser ?? false,
      impersonator: rawData.user.impersonator ? {
        id: rawData.user.impersonator.id,
        email: rawData.user.impersonator.email,
        fullName: rawData.user.impersonator.full_name,
        pictureUrl: rawData.user.impersonator.picture_url ?? null,
        createdAt: rawData.user.impersonator.created_at,
        isSuperuser: rawData.user.impersonator.is_superuser ?? false,
      } : null,
    },
  };
}

export async function fetchAllUsers(): Promise<UserListItem[]> {
  const response = await apiAxios.get("/admin/users");

  const data = UserListSchema.parse(response.data);
  return data.items;
}

export async function impersonateUser(targetUserId: string): Promise<AuthResponse> {
  const response = await apiAxios.post<RawAuthResponse>(
    "/admin/impersonate",
    { target_user_id: targetUserId }
  );

  return transformAuthResponse(response.data);
}

export async function stopImpersonation(): Promise<AuthResponse> {
  const response = await apiAxios.post<RawAuthResponse>("/admin/stop-impersonation");

  return transformAuthResponse(response.data);
}
